import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon, FileTextIcon, RefreshCwIcon, XIcon } from 'lucide-react';
interface FlowDataProps {
  onNext: () => void;
  onBack: () => void;
}
export const FlowData: React.FC<FlowDataProps> = ({
  onNext,
  onBack
}) => {
  // Mock data for flow data
  const manifoldData = {
    id: 'manifold-1',
    name: 'Manifold 1',
    storey: 'Storey 1',
    totalPressureDrop: 5.1,
    totalFlow: 5.4,
    flowLimit: 8.0,
    rooms: [{
      id: 'room-1-1',
      name: 'Room 1.1',
      floorStructure: 'Beton + fliser',
      circuit: 'SL1',
      heatOutput: 50,
      flow: 1.8,
      tt: 35,
      at: 30,
      qy: 1.8,
      tFloor: 24,
      pressureDrop: 4.2,
      length: 92.5,
      cc: 150,
      valve: 'Åben',
      status: 'normal'
    }, {
      id: 'room-1-2',
      name: 'Room 1.2',
      floorStructure: 'Beton',
      circuit: 'SL2',
      heatOutput: 50,
      flow: 1.6,
      tt: 35,
      at: 30,
      qy: 1.6,
      tFloor: 24,
      pressureDrop: 4.8,
      length: 78.3,
      cc: 150,
      valve: 'Åben',
      status: 'warning'
    }, {
      id: 'room-1-3',
      name: 'Room 1.3',
      floorStructure: 'Beton + fliser',
      circuit: 'SL3',
      heatOutput: 65,
      flow: 2.0,
      tt: 35,
      at: 30,
      qy: 2.0,
      tFloor: 26,
      pressureDrop: 4.5,
      length: 75.0,
      cc: 100,
      valve: 'Åben',
      status: 'normal'
    }],
    aiChecks: [{
      type: 'good',
      message: 'Alle kredslængder < 100 m'
    }, {
      type: 'warning',
      message: 'AP i Room 1.2 tæt på max'
    }, {
      type: 'suggestion',
      message: 'Forslag: Øg kreds til 5 i Room 1.3 eller reducer C/C til 150'
    }]
  };
  return <div className="mb-20" data-id="element-78">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6" data-id="element-79">
        <div className="p-6 border-b border-gray-200" data-id="element-80">
          <div className="flex justify-between items-center" data-id="element-81">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-82">
              Flowdata for {manifoldData.name} ({manifoldData.storey})
            </h2>
            <div className="flex space-x-2" data-id="element-83">
              <button className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-84">
                <FileTextIcon className="w-4 h-4 mr-2" data-id="element-85" />
                Vis som rapport (PDF)
              </button>
              <button className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-86">
                <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-87" />
                Rekalkuler
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-4" data-id="element-88">
            <div className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm" data-id="element-89">
              Trykfald for manifold: {manifoldData.totalPressureDrop} kPa
            </div>
            <div className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm" data-id="element-90">
              Total flow: {manifoldData.totalFlow} l/min (grænse:{' '}
              {manifoldData.flowLimit} l/min)
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4" data-id="element-91">
          <div className="col-span-3 overflow-x-auto" data-id="element-92">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-93">
              <thead data-id="element-94">
                <tr data-id="element-95">
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-96">
                    Rum
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-97">
                    Gulvstruktur
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-98">
                    SL
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-99">
                    q (W/m²)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-100">
                    Q (l/min)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-101">
                    TT (°C)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-102">
                    AT (°C)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-103">
                    QY (l/min)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-104">
                    T-floor (°C)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-105">
                    AP (kPa)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-106">
                    L (m)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-107">
                    C/C (mm)
                  </th>
                  <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-108">
                    Valve
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-109">
                {manifoldData.rooms.map(room => <tr key={room.id} className="hover:bg-gray-50" data-id="element-110">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800" data-id="element-111">
                      {room.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-112">
                      {room.floorStructure}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-113">
                      {room.circuit}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-114">
                      {room.heatOutput}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-115">
                      {room.flow}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-116">
                      {room.tt}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-117">
                      {room.at}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-118">
                      {room.qy}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-119">
                      {room.tFloor}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-120">
                      <div className={`
                        inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                        ${room.status === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}
                      `} data-id="element-121">
                        {room.pressureDrop} kPa
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-122">
                      {room.length}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-123">
                      {room.cc}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-124">
                      {room.valve}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* AI Check Panel */}
          <div className="p-6 bg-gray-50 border-l border-gray-200" data-id="element-125">
            <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center" data-id="element-126">
              <InfoIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-127" />
              Kontroltjek (AI)
            </h3>
            <div className="space-y-4" data-id="element-128">
              {manifoldData.aiChecks.map((check, index) => <div key={index} className="flex items-start" data-id="element-129">
                  {check.type === 'good' && <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-130" />}
                  {check.type === 'warning' && <AlertCircleIcon className="w-5 h-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-131" />}
                  {check.type === 'suggestion' && <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-132" />}
                  <div className="text-sm text-gray-700" data-id="element-133">{check.message}</div>
                </div>)}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-134">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-135">
                Symbolforklaring
              </h4>
              <div className="space-y-2 text-xs text-gray-600" data-id="element-136">
                <div data-id="element-137">TT: Fremløbstemperatur</div>
                <div data-id="element-138">AT: Returtemperatur</div>
                <div data-id="element-139">ΔT: Temperaturdifference</div>
                <div data-id="element-140">AP: Trykfald</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between" data-id="element-141">
        <button onClick={onBack} className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors" data-id="element-142">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-143" />
          Tilbage
        </button>
        <button onClick={onNext} className="flex items-center px-4 py-2 bg-[#E3000F] text-white rounded-md hover:bg-red-700 transition-colors" data-id="element-144">
          Fortsæt til materialeliste
          <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-145" />
        </button>
      </div>
    </div>;
};